// Identity layer. Three states: guest (anonymous), permanent, admin.
import { supabase } from "@/integrations/supabase/client";

let bootPromise: Promise<void> | null = null;

/**
 * Ensure the visitor has a Supabase identity. If none exists, sign in
 * anonymously so guest data still has a stable user_id and can sync
 * later if they upgrade. Idempotent.
 */
export function ensureIdentity(): Promise<void> {
  if (bootPromise) return bootPromise;
  bootPromise = (async () => {
    try {
      const { data } = await supabase.auth.getSession();
      if (data.session) return;
      const { error } = await supabase.auth.signInAnonymously();
      if (error) console.warn("[identity] anon sign-in failed:", error.message);
    } catch (e) {
      console.warn("[identity] bootstrap failed", e);
    }
  })();
  return bootPromise;
}

// Re-arm a guest identity whenever the session is lost (token expiry,
// remote sign-out, etc). Local-first caches survive intact, so the user
// keeps working uninterrupted — they just lose cross-device sync until
// they re-authenticate. RequireAuth / RequireAdmin screens will steer
// them to /auth when they hit a gated route.
supabase.auth.onAuthStateChange(async (event, session) => {
  if (event === "SIGNED_OUT" || (event === "TOKEN_REFRESHED" && !session)) {
    bootPromise = null;
    await ensureIdentity();
  }
});

export async function isAdminUser(email: string | null | undefined): Promise<boolean> {
  if (!email) return false;
  try {
    const { data } = await supabase
      .from("admin_users")
      .select("id")
      .eq("email", email)
      .maybeSingle();
    return !!data;
  } catch {
    return false;
  }
}
